import { useTranslation } from 'react-i18next'
import { DetailsList } from '@/components/DetailsList/DetailsList'
import { Pillow } from '@/components/Pillow/Pillow'
import { Price } from '@/components/Price'
import { ProductDetails } from '@/components/ProductCard/components/ProductDetails'
import { ProductDocuments } from '@/components/ProductCard/components/ProductDocuments'
import { ProductCardDetails } from '@/components/ProductCard/ProductCardDetails'
import { TotalPrice } from '@/components/TotalPrice/TotalPrice'
import { convertToDate } from '@/utils/date'
import { type Money } from '@/utils/formatter'
import { useFormatter } from '@/utils/useFormatter'
import { Card } from 'ui/src/components/Card/Card'
import { Divider } from 'ui/src/components/Divider/Divider'
import { Text } from 'ui/src/components/Text/Text'
import { yStack } from 'ui/src/patterns/stack'
import { type TrialContract } from './carDealership.types'

type Props = {
  contract: TrialContract
  crossedOutAmount?: Money
}

export function CarTrialContractOfferCard({ contract, crossedOutAmount }: Props) {
  const formatter = useFormatter()
  const { t } = useTranslation(['carDealership', 'cart', 'common'])

  const productDetails = contract.displayItems.map((item) => ({
    title: item.displayTitle,
    value: item.displayValue,
  }))

  const shouldShowCrossedOutAmount = crossedOutAmount !== undefined && crossedOutAmount.amount > contract.premium.amount

  const endDate = convertToDate(contract.activeTo)
  if (endDate === null) throw new Error(`Invalid end date for contract ${contract.id}`)

  const expirationDate = formatter.shortDate(endDate)

  return (
    <Card.Root>
      <Card.Header>
        <Card.Media>
          <Pillow size="small" role="presentation" src={contract.pillowSrc} alt="" />
        </Card.Media>

        <Card.Heading>
          <Card.Title variant={{ _: 'standard.16', sm: 'standard.18' }}>
            {contract.productVariant.displayName}
          </Card.Title>
          <Card.Subtitle size={{ _: 'sm', sm: 'md' }}>{contract.exposureDisplayName}</Card.Subtitle>
        </Card.Heading>
      </Card.Header>

      <ProductCardDetails.Root>
        <ProductCardDetails.Trigger>
          {(isOpen) =>
            isOpen ? t('HIDE_DETAILS_BUTTON_LABEL', { ns: 'cart' }) : t('SHOW_DETAILS_BUTTON_LABEL', { ns: 'cart' })
          }
        </ProductCardDetails.Trigger>

        <ProductCardDetails.Content className={yStack({ paddingBlock: 'md', gap: 'md' })}>
          <ProductDetails details={productDetails} />
          <ProductDocuments documents={contract.productVariant.documents} />
        </ProductCardDetails.Content>
      </ProductCardDetails.Root>

      <DetailsList.Root>
        <DetailsList.Item>
          <DetailsList.Label>{contract.productVariant.displayName}</DetailsList.Label>
          <DetailsList.Value>
            <Price
              currencyCode={contract.premium.currencyCode}
              amount={shouldShowCrossedOutAmount ? crossedOutAmount.amount : contract.premium.amount}
              reducedAmount={shouldShowCrossedOutAmount ? contract.premium.amount : undefined}
            />
          </DetailsList.Value>
        </DetailsList.Item>

        <DetailsList.Item>
          <DetailsList.Label>{t('TRIAL_TERMINATION_DATE_MESSAGE')}</DetailsList.Label>
          <DetailsList.Value>
            <Text as="span" size="xs">
              {expirationDate}
            </Text>
          </DetailsList.Value>
        </DetailsList.Item>
      </DetailsList.Root>

      <Divider />

      <TotalPrice
        currencyCode={contract.premium.currencyCode}
        amount={shouldShowCrossedOutAmount ? crossedOutAmount.amount : contract.premium.amount}
        reducedAmount={shouldShowCrossedOutAmount ? contract.premium.amount : undefined}
        label={t('common:YOUR_PRICE')}
      />
    </Card.Root>
  )
}
